import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import "./NcdForm.css";
import { ncdAPI } from "../../../services/ncd.api";
import { useNCD } from "../hooks/useNCD";
import { formatToInputDate, formatToApiDate } from "../../../helpers/common-helper";

export const OralScreeningCHO = ({ individualId, oralScreeningId, viewMode, onIdUpdate, selectedUser, setUserError }) => {
    const { setLoading, setError } = useNCD();
    const today = new Date().toISOString().split("T")[0];
    const [oralData, setOralData] = useState({});

    // ======================================Use Oral JSX ========================

    const [pointToConditionMap, setPointToConditionMap] = useState({});

    // Simulate getting data from API
    const fetchData = useCallback(async (oralScreeningId) => {
        try {
            setLoading(true);
            const responseData = await ncdAPI.getOralScreeningCHO(individualId, oralScreeningId);
            // setScreeningData('oral', responseData?.data);
            setOralData(responseData?.data);

            // Prepare point-to-condition map
            const tempMap = {};

            const mapCondition = (conditionFlag, points, label) => {
                if (conditionFlag && Array.isArray(points)) {
                    points.forEach((pt) => {
                        if (!tempMap[pt]) tempMap[pt] = [];
                        tempMap[pt].push(label);
                    });
                }
            };

            const ex = responseData?.data?.examination;

            mapCondition(ex?.nonHealingUlcer, ex?.nonHealingUlcerPoints, "Non-healing ulcers");
            mapCondition(ex?.whitePatches, ex?.whitePatchesPoints, "White Patches");
            mapCondition(ex?.redPatches, ex?.redPatchesPoints, "Red Patches");
            mapCondition(ex?.growth, ex?.growthPoints, "Growth of recent origin");

            setPointToConditionMap(tempMap);

            // Initialize dotValues based on all conditions
            const initialDotValues = {};
            for (let i = 1; i <= totalDots; i++) {
                const dotKey = `dot${i}`;
                const opKey = `OP${i}`;
                if (tempMap[opKey] && tempMap[opKey].length > 0) {
                    initialDotValues[dotKey] = tempMap[opKey];
                }
            }

            setDotValues(initialDotValues);

            setLoading(false);
        } catch (err) {
            setError("Failed to fetch oral screening data");
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [individualId]);

    // Fetch data when in VIEW mode and ID is available
    useEffect(() => {
        if (viewMode === "VIEW" && oralScreeningId) {
            fetchData(oralScreeningId);
        }
    }, [viewMode, oralScreeningId, fetchData]);

    const [formData, setFormData] = useState({
        assessmentDate: today,
        exam: {
            abnormalPatches: false,
            noAbnormalityFound: false,
            examDate: '', // can be same as assessmentDate or separate
            cavity: '',
            chronicTrauma: '',
            dentalCaries: null,
            dentures: null,
            lossOfTooth: null,
            fissures: null,
            induration: null,
            lymphadenopathy: null,
            otherFindings: false,
            otherFindingsTxt: '',
            rootCanalHistory: '',
            site: '',
            visualExam: {
                leukoplakiaPatch: [],
                erythroplakiaPatch: [],
                ulcerPatch: [],
                growthPatch: [],
                lockedjawPatch: [],
                subfibrosisPatch: [],
                smokerspalatePatch: [],
                lichenPlanusPatch: []
            }
        }
    });

    const [hasOralSymptoms, setHasOralSymptoms] = useState("no");
    const handleOralChange = (e) => {
        setHasOralSymptoms(e.target.value);
        setSelectedSymptoms((prev) => ({
            ...prev,
            issueMouth: e.target.value
        }));
    };

    const isDisabled = hasOralSymptoms === "no";

    const [refer, setRefer] = useState("");
    const [referTo, setReferTo] = useState("");

    const handleReferChange = (value) => {
        setRefer(value);
        if (value === "No") {
            setReferTo("");
            setReferralOther("");
        }
    };

    const handleReferToChange = (value) => {
        setReferTo(value);
        if (value !== "Others") {
            setReferralOther("");
        }
    };

    // Add state for remarks fields
    const [symptomRemarks, setSymptomRemarks] = useState("");
    const [visualRemarks, setVisualRemarks] = useState("");
    const [referralRemarks, setReferralRemarks] = useState("");
    const [referralOther, setReferralOther] = useState("");

    // ======================================Use Oral JSX END ========================

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedUser) {
            setUserError(true);
        }

        // Build payload as per the required structure
        const payload = {
            symptoms: {
                anyIssueInMouth: selectedSymptoms["issueMouth"] === "yes",
                difficultyInOpeningMouth: selectedSymptoms["difficultyInOpeningMouth"] === "Yes",
                difficultyInSpicyFood: selectedSymptoms["difficultyInSpicyFood"] === "Yes",
                whiteRedPatch: selectedSymptoms["whiteRedPatch"] === "Yes",
                changeInVoice: selectedSymptoms["changeInVoice"] === "Yes",
                ulceration: selectedSymptoms["ulceration"] === "Yes",
                remarks: symptomRemarks,
                date: formatToApiDate(formData.assessmentDate)
            },
            examination: {
                restrictedMouthOpening: selectedSymptoms["Mouth"] === "Yes",
                nonHealingUlcer: selectedSymptoms["Healing"] === "Yes",
                nonHealingUlcerPoints: Object.entries(dotValues)
                    .filter(([_, v]) => v && v.includes("Non-healing ulcers"))
                    .map(([k]) => k.replace("dot", "OP")),
                whitePatches: selectedSymptoms["WhitePatch"] === "Yes",
                whitePatchesPoints: Object.entries(dotValues)
                    .filter(([_, v]) => v && v.includes("White Patches"))
                    .map(([k]) => k.replace("dot", "OP")),
                redPatches: selectedSymptoms["RedPatch"] === "Yes",
                redPatchesPoints: Object.entries(dotValues)
                    .filter(([_, v]) => v && v.includes("Red Patches"))
                    .map(([k]) => k.replace("dot", "OP")),
                growth: selectedSymptoms["Growth"] === "Yes",
                growthPoints: Object.entries(dotValues)
                    .filter(([_, v]) => v && v.includes("Growth of recent origin"))
                    .map(([k]) => k.replace("dot", "OP")),
                remarks: visualRemarks
            },
            referral: {
                referred: refer === "Yes",
                referredTo: referTo || null,
                referredToIfOther: referralOther || null,
                remarks: referralRemarks
            },
            createdUser: selectedUser || "",
            screeningANM: {
                users: selectedUser || "",
                anmValue: selectedUser || "",
                viaTrained: null
            },
            currentScreeningId: oralScreeningId,
            screeningType: "FirstTime Screening"
        };

        try {
            setLoading(true);
            const response = await ncdAPI.submitOralScreeningCHO(individualId, payload);

            if (response.success) {
                if (response?.data?.screeningId) {
                    onIdUpdate(response?.data?.screeningId);
                }
                toast.success(`Oral cancer screening data has saved successfully`);

                // Clear form fields
                setFormData({ assessmentDate: today, exam: { abnormalPatches: false, noAbnormalityFound: false, examDate: '', cavity: '', chronicTrauma: '', dentalCaries: null, dentures: null, lossOfTooth: null, fissures: null, induration: null, lymphadenopathy: null, otherFindings: false, otherFindingsTxt: '', rootCanalHistory: '', site: '', visualExam: { leukoplakiaPatch: [], erythroplakiaPatch: [], ulcerPatch: [], growthPatch: [], lockedjawPatch: [], subfibrosisPatch: [], smokerspalatePatch: [], lichenPlanusPatch: [] } } });
                setHasOralSymptoms("no");
                setSelectedSymptoms({});
                setDotValues({});
                setSelectedFinding("");
                setRefer("");
                setReferTo("");
                setSymptomRemarks("");
                setVisualRemarks("");
                setReferralRemarks("");
                setReferralOther("");

                setOralData(prev => ({
                    ...prev,
                    examination: {}
                }));

            } else {
                toast.error(response.message || "Failed to save data", { className: "custom-toast-error" });
            }
            setLoading(false);
        } catch (error) {
            toast.error(error.response?.data?.message || "Something went wrong", { className: "custom-toast-error" });
            setLoading(false);
        }
    };

    const symptomsData = useMemo(() => [
        { name: "WhitePatch", label: "White patches?", dropdowntext: "White Patches", img: "/images/Whitepatches.png", viewname: "whitePatches" },
        { name: "RedPatch", label: "Red patches?", dropdowntext: "Red Patches", img: "/images/Redpatches.png", viewname: "redPatches" },
        { name: "Healing", label: "Non healing ulcers?", dropdowntext: "Non-healing ulcers", img: "/images/ulceration.png", viewname: "nonHealingUlcer" },
        { name: "Growth", label: "Growth of recent origin?", dropdowntext: "Growth of recent origin", img: "/images/growth_of_recent_origin.png", viewname: "growth" },
    ], []);

    const [selectedSymptoms, setSelectedSymptoms] = useState({});

    const handleOralVisExamiSel = (name, value) => {
        setSelectedSymptoms((prev) => ({ ...prev, [name]: value }));
    };

    // Only return symptomsData items that are selected "Yes"
    const getSelectedOptions = useCallback(() => {
        return symptomsData
            .filter(symptom => selectedSymptoms[symptom.name] === "Yes")
            .map(symptom => symptom.dropdowntext);
    }, [selectedSymptoms, symptomsData]);

    const [selectedFinding, setSelectedFinding] = useState("");
    const [dotValues, setDotValues] = useState({});
    const totalDots = 16;

    useEffect(() => {
        const options = getSelectedOptions();
        if (options.length === 0) {
            setSelectedFinding("");
        } else if (!selectedFinding && options.length === 1) {
            setSelectedFinding(options[0]);
        } else if (selectedFinding && !options.includes(selectedFinding)) {
            setSelectedFinding("");
        }
    }, [selectedSymptoms, getSelectedOptions, selectedFinding]);

    useEffect(() => {
        setDotValues((prev) => {
            const updated = { ...prev };
            Object.keys(updated).forEach((dotKey) => {
                const filtered = updated[dotKey].filter((finding) => getSelectedOptions().includes(finding));
                if (filtered.length === 0) {
                    delete updated[dotKey];
                } else {
                    updated[dotKey] = filtered;
                }
            });
            return updated;
        });
    }, [selectedSymptoms, getSelectedOptions]);

    const handleDotClick = (dotKey) => {
        if (!selectedFinding || !getSelectedOptions().includes(selectedFinding)) return;

        setDotValues((prev) => {
            const currentValues = prev[dotKey] || [];
            const isSelected = currentValues.includes(selectedFinding);

            if (isSelected) {
                const updated = currentValues.filter(v => v !== selectedFinding);
                return updated.length > 0 ? { ...prev, [dotKey]: updated } : { ...prev, [dotKey]: undefined };
            }
            return { ...prev, [dotKey]: [...currentValues, selectedFinding] };
        });
    };

    return (
        <>

            {viewMode === "ADD" ? (
                <>
                    <form onSubmit={handleSubmit}>

                        <div className="oral-cancer-table">
                            <div className="row">
                                <div className="col-xs-12 col-md-8"></div>
                                <div className="col-xs-12 col-md-4">
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={formData.assessmentDate}
                                        max={new Date().toISOString().split('T')[0]}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                assessmentDate: e.target.value
                                            }))
                                        }
                                        required
                                    />
                                </div>
                            </div>
                            <div className="headerTitle">
                                <div className="titleSection"><h4>Symptoms</h4></div>
                            </div>
                            <div className="choCancerFeildsSection">
                                <div className="row">
                                    <div className="col-xs-12 col-md-4">
                                        <div className="SymptomsFeild">
                                            <div className="symptomsImg">
                                                <img src={"/images/any_growth_in_mouth_no_healing.png"} alt="Oral mouth issue" />
                                            </div>
                                            <div className="symptomsDetaisl">
                                                <div className="symbtomsLabel">
                                                    <p>Any issue in mouth?<span className="Refre-red">*</span></p>
                                                </div>
                                                <div className="symtomeFeildsForm">
                                                    <label>
                                                        <input type="radio" name="issueMouth" className="form-check-input" value="yes" checked={hasOralSymptoms === "yes"} onChange={handleOralChange} />
                                                        <span>Yes</span>
                                                    </label>
                                                    <label>
                                                        <input type="radio" name="issueMouth" className="form-check-input" value="no" checked={hasOralSymptoms === "no"} onChange={handleOralChange} />
                                                        <span>No</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-xs-12 col-md-4">
                                        <div className="SymptomsFeild">
                                            <div className="symptomsImg">
                                                <img src={"/images/any_white_or_red_patch_in_mouth.png"} alt="White patch" />
                                            </div>
                                            <div className="symptomsDetaisl">
                                                <div className="symbtomsLabel">
                                                    <p>White/Red Patch in Oral Cavity?</p>
                                                </div>
                                                <div className="symtomeFeildsForm">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="whiteRedPatch"
                                                            className="form-check-input"
                                                            value="Yes"
                                                            disabled={isDisabled}
                                                            checked={selectedSymptoms["whiteRedPatch"] === "Yes"}
                                                            onChange={() => handleOralVisExamiSel("whiteRedPatch", "Yes")}
                                                        />
                                                        <span>Yes</span>
                                                    </label>
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="whiteRedPatch"
                                                            className="form-check-input"
                                                            value="No"
                                                            disabled={isDisabled}
                                                            checked={selectedSymptoms["whiteRedPatch"] === "No"}
                                                            onChange={() => handleOralVisExamiSel("whiteRedPatch", "No")}
                                                        />
                                                        <span>No</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-xs-12 col-md-4">
                                        <div className="SymptomsFeild">
                                            <div className="symptomsImg">
                                                <img src={"/images/Spicyfood.png"} alt="Spicy food" />
                                            </div>
                                            <div className="symptomsDetaisl">
                                                <div className="symbtomsLabel">
                                                    <p>Difficulty in tolerating spicy food?</p>
                                                </div>
                                                <div className="symtomeFeildsForm">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="difficultyInSpicyFood"
                                                            className="form-check-input"
                                                            value="Yes"
                                                            disabled={isDisabled}
                                                            checked={selectedSymptoms["difficultyInSpicyFood"] === "Yes"}
                                                            onChange={() => handleOralVisExamiSel("difficultyInSpicyFood", "Yes")}
                                                        />
                                                        <span>Yes</span>
                                                    </label>
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="difficultyInSpicyFood"
                                                            className="form-check-input"
                                                            value="No"
                                                            disabled={isDisabled}
                                                            checked={selectedSymptoms["difficultyInSpicyFood"] === "No"}
                                                            onChange={() => handleOralVisExamiSel("difficultyInSpicyFood", "No")}
                                                        />
                                                        <span>No</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-xs-12 col-md-4">
                                        <div className="SymptomsFeild">
                                            <div className="symptomsImg">
                                                <img src={"/images/Difficulty-opening-mouth.png"} alt="Difficulty in mouth opening" />
                                            </div>
                                            <div className="symptomsDetaisl">
                                                <div className="symbtomsLabel">
                                                    <p>Difficulty in opening mouth?</p>
                                                </div>
                                                <div className="symtomeFeildsForm">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="difficultyInOpeningMouth"
                                                            className="form-check-input"
                                                            value="Yes"
                                                            disabled={isDisabled}
                                                            checked={selectedSymptoms["difficultyInOpeningMouth"] === "Yes"}
                                                            onChange={() => handleOralVisExamiSel("difficultyInOpeningMouth", "Yes")}
                                                        />
                                                        <span>Yes</span>
                                                    </label>
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="difficultyInOpeningMouth"
                                                            className="form-check-input"
                                                            value="No"
                                                            disabled={isDisabled}
                                                            checked={selectedSymptoms["difficultyInOpeningMouth"] === "No"}
                                                            onChange={() => handleOralVisExamiSel("difficultyInOpeningMouth", "No")}
                                                        />
                                                        <span>No</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-xs-12 col-md-4">
                                        <div className="SymptomsFeild">
                                            <div className="symptomsImg">
                                                <img src={"/images/sudden-change-in-voice.png"} alt="Sudden change in voice" />
                                            </div>
                                            <div className="symptomsDetaisl">
                                                <div className="symbtomsLabel">
                                                    <p>Change in voice/Hoarseness?</p>
                                                </div>
                                                <div className="symtomeFeildsForm">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="changeInVoice"
                                                            className="form-check-input"
                                                            value="Yes"
                                                            disabled={isDisabled}
                                                            checked={selectedSymptoms["changeInVoice"] === "Yes"}
                                                            onChange={() => handleOralVisExamiSel("changeInVoice", "Yes")}
                                                        />
                                                        <span>Yes</span>
                                                    </label>
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="changeInVoice"
                                                            className="form-check-input"
                                                            value="No"
                                                            disabled={isDisabled}
                                                            checked={selectedSymptoms["changeInVoice"] === "No"}
                                                            onChange={() => handleOralVisExamiSel("changeInVoice", "No")}
                                                        />
                                                        <span>No</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-xs-12 col-md-4">
                                        <div className="SymptomsFeild">
                                            <div className="symptomsImg">
                                                <img src={"/images/ulceration.png"} alt="Ulceration in mouth" />
                                            </div>
                                            <div className="symptomsDetaisl">
                                                <div className="symbtomsLabel">
                                                    <p>Ulceration/roughened areas in mouth for more than 3 weeks?</p>
                                                </div>
                                                <div className="symtomeFeildsForm">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="ulceration"
                                                            className="form-check-input"
                                                            value="Yes"
                                                            disabled={isDisabled}
                                                            checked={selectedSymptoms["ulceration"] === "Yes"}
                                                            onChange={() => handleOralVisExamiSel("ulceration", "Yes")}
                                                        />
                                                        <span>Yes</span>
                                                    </label>
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="ulceration"
                                                            className="form-check-input"
                                                            value="No"
                                                            disabled={isDisabled}
                                                            checked={selectedSymptoms["ulceration"] === "No"}
                                                            onChange={() => handleOralVisExamiSel("ulceration", "No")}
                                                        />
                                                        <span>No</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-xs-12 col-md-4 remarksRow">
                                        <div className="SymptomsFeild">
                                            <div className="symptomsImg">
                                                <img src={"/images/Remarks.png"} alt="" />
                                            </div>
                                            <div className="symptomsDetaisl">
                                                <div className="symbtomsLabel">
                                                    <p>Remarks</p>
                                                </div>
                                                <div className="symtomeFeildsForm">
                                                    <textarea type="text" rows="2" value={symptomRemarks} onChange={e => setSymptomRemarks(e.target.value)} ></textarea>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="headerTitle ">
                                <div className="titleSection"><h4>Visual Examination</h4></div>
                            </div>
                            <div className="choCancerFeildsSection">
                                <div className="row">

                                    {symptomsData.map((symptom) => (
                                        <div className="col-xs-12 col-md-4" key={symptom.name}>
                                            <div className="SymptomsFeild">
                                                <div className="symptomsImg">
                                                    <img src={symptom.img} alt={symptom.label} />
                                                </div>
                                                <div className="symptomsDetaisl">
                                                    <div className="symbtomsLabel">
                                                        <p>{symptom.label}</p>
                                                    </div>
                                                    <div className="symtomeFeildsForm">
                                                        <label>
                                                            <input
                                                                type="radio"
                                                                className="form-check-input"
                                                                name={symptom.name}
                                                                checked={selectedSymptoms[symptom.name] === "Yes"}
                                                                onChange={() => handleOralVisExamiSel(symptom.name, "Yes")}
                                                            />
                                                            <span>Yes</span>
                                                        </label>
                                                        <label>
                                                            <input
                                                                type="radio"
                                                                className="form-check-input"
                                                                name={symptom.name}
                                                                checked={selectedSymptoms[symptom.name] === "No"}
                                                                onChange={() => handleOralVisExamiSel(symptom.name, "No")}
                                                            />
                                                            <span>No</span>
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    <div className="col-xs-12 col-md-4">
                                        <div className="SymptomsFeild">
                                            <div className="symptomsImg">
                                                <img src={"/images/Difficulty-opening-mouth.png"} alt="Difficulty in mouth opening" />
                                            </div>
                                            <div className="symptomsDetaisl">
                                                <div className="symbtomsLabel">
                                                    <p>Restricted mouth opening?</p>
                                                </div>
                                                <div className="symtomeFeildsForm">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="Mouth"
                                                            className="form-check-input"
                                                            value="Yes"
                                                            checked={selectedSymptoms["Mouth"] === "Yes"}
                                                            onChange={() => handleOralVisExamiSel("Mouth", "Yes")}
                                                        />
                                                        <span>Yes</span>
                                                    </label>
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="Mouth"
                                                            className="form-check-input"
                                                            value="No"
                                                            checked={selectedSymptoms["Mouth"] === "No"}
                                                            onChange={() => handleOralVisExamiSel("Mouth", "No")}
                                                        />
                                                        <span>No</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-xs-12 col-md-4 remarksRow">
                                        <div className="SymptomsFeild">
                                            <div className="symptomsImg">
                                                <img src={"/images/Remarks.png"} alt="" />
                                            </div>
                                            <div className="symptomsDetaisl">
                                                <div className="symbtomsLabel">
                                                    <p>Remarks</p>
                                                </div>
                                                <div className="symtomeFeildsForm">
                                                    <textarea type="text" rows="2" value={visualRemarks} onChange={e => setVisualRemarks(e.target.value)} ></textarea>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mouthDemo mouthDemoCHO my-4">

                                <div className="headerSectionBreastCHO">
                                    <div className="feildsArea">
                                        <select value={selectedFinding} onChange={(e) => setSelectedFinding(e.target.value)}>
                                            {getSelectedOptions().length === 0 ? (
                                                <option value="">Select Findings</option>
                                            ) : (
                                                <>
                                                    {getSelectedOptions().map((item, index) => (
                                                        <option key={index} value={item}>
                                                            {item}
                                                        </option>
                                                    ))}
                                                </>
                                            )}
                                        </select>
                                    </div>
                                    <div className="breastheadings">
                                        <h5>Select findings from dropdown and mark the points on the mouth</h5>
                                    </div>
                                </div>

                                <div className="mouth-wrapper">
                                    {Array.from({ length: totalDots }, (_, i) => {
                                        const dotKey = `dot${i + 1}`;
                                        const values = dotValues[dotKey];
                                        const isSelected = values && values.length > 0;

                                        return (
                                            <div
                                                key={dotKey}
                                                className={`dot ${dotKey} ${isSelected ? "active" : ""}`}
                                                onClick={() => handleDotClick(dotKey)}
                                                title={isSelected ? values.join(", ") : ""}
                                            >
                                                {isSelected && (
                                                    <span className="dot-text">
                                                        {values.map((val, idx) => (
                                                            <div key={idx} className="mouth-dot-item">{val}</div>
                                                        ))}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                {/* <button>Show all findings</button> */}
                            </div>

                            <div className="headerTitle ">
                                <div className="titleSection"><h4>Referral</h4></div>
                            </div>
                            <div className="choCancerFeildsSection">
                                <div className="row">
                                    <div className="col-xs-12 col-md-4">
                                        <div className="SymptomsFeild">
                                            <div className="symptomsImg">
                                                <img src={"/images/Refer.png"} alt="" />
                                            </div>
                                            <div className="symptomsDetaisl">
                                                <div className="symbtomsLabel">
                                                    <p>Refer</p>
                                                </div>
                                                <div className="symtomeFeildsForm">
                                                    <label>
                                                        <input type="radio" name="oralRefer" className="form-check-input" value="Yes" checked={refer === "Yes"} onChange={(e) => handleReferChange(e.target.value)}
                                                        />
                                                        <span>Yes</span>
                                                    </label>
                                                    <label>
                                                        <input type="radio" name="oralRefer" className="form-check-input" value="No" checked={refer === "No"} onChange={(e) => handleReferChange(e.target.value)}
                                                        />
                                                        <span>No</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-xs-12 col-md-6">
                                        <div className="SymptomsFeild">
                                            <div className="symptomsImg">
                                                <img src={"/images/Refer.png"} alt="" />
                                            </div>
                                            <div className="symptomsDetaisl ">
                                                <div className="symbtomsLabel">
                                                    <p> Refer to {refer === "Yes" && <span className="text-danger">*</span>} </p>
                                                </div>
                                                <div className="symtomeFeildsForm">
                                                    <select
                                                        onChange={(e) => handleReferToChange(e.target.value)}
                                                        value={referTo}
                                                        disabled={refer !== "Yes"}
                                                    >
                                                        <option>Select</option>
                                                        <option value="PHC">PHC</option>
                                                        <option value="CHC">CHC</option>
                                                        <option value="District hospital">District Hospital</option>
                                                        <option value="Others">Other</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="symptomsDetaisl">
                                                <div className="symbtomsLabel">
                                                    <p>If other, specify</p>
                                                </div>
                                                <div className="symtomeFeildsForm">
                                                    <input
                                                        type="text"
                                                        value={referralOther}
                                                        onChange={e => setReferralOther(e.target.value)}
                                                        disabled={referTo !== "Others"}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-xs-12 col-md-12">
                                        <div className="SymptomsFeild remarksRow">
                                            <div className="symptomsImg">
                                                <img src={"/images/Remarks.png"} alt="" />
                                            </div>
                                            <div className="symptomsDetaisl">
                                                <div className="symbtomsLabel">
                                                    <p>Remarks</p>
                                                </div>
                                                <div className="symtomeFeildsForm">
                                                    <textarea type="text" rows="2" value={referralRemarks} onChange={e => setReferralRemarks(e.target.value)} ></textarea>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="save-button-container">
                            <button type="submit" className="save-button">Save & Continue</button>
                        </div>

                    </form>

                </>

            ) : (
                <>
                    <div className="oral-cancer-table">
                        <div className="row">
                            <div className="col-xs-12 col-md-8"></div>
                            <div className="col-xs-12 col-md-4">
                                <input
                                    type="date"
                                    className="form-control"
                                    value={formatToInputDate(oralData?.symptoms?.date)}
                                    disabled
                                />
                            </div>
                        </div>
                        <div className="headerTitle">
                            <div className="titleSection"><h4>Symptoms</h4></div>
                        </div>
                        <div className="choCancerFeildsSection">
                            <div className="row">
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/any_growth_in_mouth_no_healing.png"} alt="Oral mouth issue" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Any issue in mouth?<span className="Refre-red">*</span></p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="issueMouth" className="form-check-input" checked={oralData?.symptoms?.anyIssueInMouth === true} disabled />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="issueMouth" className="form-check-input" checked={oralData?.symptoms?.anyIssueInMouth === false} disabled />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/any_white_or_red_patch_in_mouth.png"} alt="White patch" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>White/Red Patch in Oral Cavity?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="whiteRedPatch"
                                                        className="form-check-input"
                                                        checked={oralData?.symptoms?.whiteRedPatch === true}
                                                        disabled
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="whiteRedPatch"
                                                        className="form-check-input"
                                                        checked={oralData?.symptoms?.whiteRedPatch === false}
                                                        disabled
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Spicyfood.png"} alt="Spicy food" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Difficulty in tolerating spicy food?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="difficultyInSpicyFood"
                                                        className="form-check-input"
                                                        checked={oralData?.symptoms?.difficultyInSpicyFood === true}
                                                        disabled
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="difficultyInSpicyFood"
                                                        className="form-check-input"
                                                        checked={oralData?.symptoms?.difficultyInSpicyFood === false}
                                                        disabled
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Difficulty-opening-mouth.png"} alt="Difficulty in mouth opening" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Difficulty in opening mouth?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="difficultyInOpeningMouth"
                                                        className="form-check-input"
                                                        checked={oralData?.symptoms?.difficultyInOpeningMouth === true}
                                                        disabled
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="difficultyInOpeningMouth"
                                                        className="form-check-input"
                                                        checked={oralData?.symptoms?.difficultyInOpeningMouth === false}
                                                        disabled
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/sudden-change-in-voice.png"} alt="Sudden change in voice" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Change in voice/Hoarseness?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="changeInVoice"
                                                        className="form-check-input"
                                                        checked={oralData?.symptoms?.changeInVoice === true}
                                                        disabled
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="changeInVoice"
                                                        className="form-check-input"
                                                        checked={oralData?.symptoms?.changeInVoice === false}
                                                        disabled
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/ulceration.png"} alt="Ulceration in mouth" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Ulceration/roughened areas in mouth for more than 3 weeks?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="ulceration"
                                                        className="form-check-input"
                                                        checked={oralData?.symptoms?.ulceration === true}
                                                        disabled
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="ulceration"
                                                        className="form-check-input"
                                                        checked={oralData?.symptoms?.ulceration === false}
                                                        disabled
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4 remarksRow">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Remarks.png"} alt="" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Remarks</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <textarea type="text" rows="2" value={oralData?.symptoms?.remarks} disabled></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="headerTitle ">
                            <div className="titleSection"><h4>Visual Examination</h4></div>
                        </div>
                        <div className="choCancerFeildsSection">
                            <div className="row">

                                {symptomsData.map((symptom) => (
                                    <div className="col-xs-12 col-md-4" key={symptom.name}>
                                        <div className="SymptomsFeild">
                                            <div className="symptomsImg">
                                                <img src={symptom.img} alt={symptom.label} />
                                            </div>
                                            <div className="symptomsDetaisl">
                                                <div className="symbtomsLabel">
                                                    <p>{symptom.label}</p>
                                                </div>
                                                <div className="symtomeFeildsForm">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name={symptom.name}
                                                            className="form-check-input"
                                                            checked={oralData?.examination?.[symptom.viewname] === true}
                                                            disabled
                                                        />
                                                        <span>Yes</span>
                                                    </label>
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name={symptom.name}
                                                            className="form-check-input"
                                                            checked={oralData?.examination?.[symptom.viewname] === false}
                                                            disabled
                                                        />
                                                        <span>No</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Difficulty-opening-mouth.png"} alt="Difficulty in mouth opening" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Restricted mouth opening?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="Mouth"
                                                        className="form-check-input"
                                                        checked={oralData?.examination?.restrictedMouthOpening === true}
                                                        disabled
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="Mouth"
                                                        className="form-check-input"
                                                        checked={oralData?.examination?.restrictedMouthOpening === false}
                                                        disabled
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-xs-12 col-md-4 remarksRow">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Remarks.png"} alt="" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Remarks</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <textarea type="text" rows="2" value={oralData?.examination?.remarks} disabled></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mouthDemo mouthDemoCHO my-4">

                            <div className="headerSectionBreastCHO">
                                <div className="feildsArea">
                                    <select disabled>
                                        <option value="">Select Findings</option>
                                    </select>
                                </div>
                                <div className="breastheadings">
                                    <h5>Select findings from dropdown and mark the points on the mouth</h5>
                                </div>
                            </div>

                            <div className="mouth-wrapper">
                                {Array.from({ length: totalDots }, (_, i) => {
                                    const dotKey = `OP${i + 1}`;
                                    const dotClass = `dot${i + 1}`;
                                    const conditions = pointToConditionMap[dotKey];

                                    return (
                                        <div
                                            key={dotKey}
                                            className={`dot ${dotClass} ${conditions ? "active" : ""}`}
                                            title={conditions ? conditions.join(", ") : ""}
                                        >
                                            {conditions && (
                                                <span className="dot-text">
                                                    {conditions.map((cond, idx) => (
                                                        <div key={idx} className="mouth-dot-item">{cond}</div>
                                                    ))}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                        </div>

                        <div className="headerTitle ">
                            <div className="titleSection"><h4>Referral</h4></div>
                        </div>
                        <div className="choCancerFeildsSection">
                            <div className="row">
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Refer.png"} alt="" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Refer</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="oralRefer" className="form-check-input" checked={oralData?.referral?.referred === true} disabled
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="oralRefer" className="form-check-input" checked={oralData?.referral?.referred === false} disabled
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-6">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Refer.png"} alt="" />
                                        </div>
                                        <div className="symptomsDetaisl ">
                                            <div className="symbtomsLabel">
                                                <p>Refer to {refer === "Yes" && <span className="text-danger">*</span>} </p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <select
                                                    value={oralData?.referral?.referredTo} disabled
                                                >
                                                    <option>Select</option>
                                                    <option value="PHC">PHC</option>
                                                    <option value="CHC">CHC</option>
                                                    <option value="District hospital">District Hospital</option>
                                                    <option value="Others">Other</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>If other, specify</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <input
                                                    type="text"
                                                    value={oralData?.referral?.referredToIfOther}
                                                    disabled
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-12">
                                    <div className="SymptomsFeild remarksRow">
                                        <div className="symptomsImg">
                                            <img src={"/images/Remarks.png"} alt="" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Remarks</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <textarea type="text" rows="2" value={oralData?.referral?.remarks} disabled></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
            <div className="separator"></div>
        </>
    );
};
